import React, { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import './SpecAccessDialog.scss';

const SpecAccessDialog = ({
  isOpen,
  onClose,
  getComponent,
  backendSpecsActions,
  backendSpecsSelectors,
}) => {
  const [selectedSpecId, setSelectedSpecId] = useState('');
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [granting, setGranting] = useState(false);

  const Modal = getComponent('Modal', true);
  const ModalHeader = getComponent('ModalHeader', true);
  const ModalTitle = getComponent('ModalTitle', true);
  const ModalBody = getComponent('ModalBody', true);
  const ModalFooter = getComponent('ModalFooter', true);

  const specsList = backendSpecsSelectors?.selectSpecsList?.() || [];

  useEffect(() => {
    if (isOpen && backendSpecsActions?.fetchSpecsList) {
      backendSpecsActions.fetchSpecsList();
    }
  }, [isOpen, backendSpecsActions]);

  const loadAccess = useCallback(async (specId) => {
    if (!specId || !backendSpecsActions?.fetchSpecAccess) return;
    setLoading(true);
    setError(null);
    try {
      const data = await backendSpecsActions.fetchSpecAccess(Number(specId));
      setAccessList(data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load access list');
      setAccessList([]);
    } finally {
      setLoading(false);
    }
  }, [backendSpecsActions]);

  useEffect(() => {
    if (selectedSpecId) {
      loadAccess(selectedSpecId);
    } else {
      setAccessList([]);
    }
  }, [selectedSpecId, loadAccess]);

  const handleGrant = useCallback(async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !selectedSpecId || !backendSpecsActions?.grantSpecAccess) return;
    setGranting(true);
    setError(null);
    try {
      await backendSpecsActions.grantSpecAccess(newUsername.trim(), Number(selectedSpecId));
      setNewUsername('');
      await loadAccess(selectedSpecId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to grant access');
    } finally {
      setGranting(false);
    }
  }, [newUsername, selectedSpecId, backendSpecsActions, loadAccess]);

  const handleRevoke = useCallback(async (username) => {
    if (!backendSpecsActions?.revokeSpecAccess) return;
    setError(null);
    try {
      await backendSpecsActions.revokeSpecAccess(username, Number(selectedSpecId));
      await loadAccess(selectedSpecId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to revoke access');
    }
  }, [selectedSpecId, backendSpecsActions, loadAccess]);

  const handleSetDefault = useCallback(async (username) => {
    if (!backendSpecsActions?.setUserDefaultSpec) return;
    setError(null);
    try {
      await backendSpecsActions.setUserDefaultSpec(username, Number(selectedSpecId));
      await loadAccess(selectedSpecId);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to set default spec');
    }
  }, [selectedSpecId, backendSpecsActions, loadAccess]);

  const handleClose = useCallback(() => {
    setSelectedSpecId('');
    setAccessList([]);
    setError(null);
    setNewUsername('');
    onClose();
  }, [onClose]);

  if (!isOpen || !Modal) return null;

  return (
    <Modal isOpen={isOpen} contentLabel="User Access">
      {ModalHeader && ModalTitle && (
        <ModalHeader>
          <ModalTitle>User Access Management</ModalTitle>
        </ModalHeader>
      )}
      {ModalBody && (
        <ModalBody>
          <div className="spec-access-container">
            <div className="spec-access-selector">
              <label className="spec-access-selector__label">
                <span>Select Spec:</span>
                <select
                  value={selectedSpecId}
                  onChange={(e) => setSelectedSpecId(e.target.value)}
                  className="spec-access-selector__dropdown"
                >
                  <option value="">-- Choose a spec --</option>
                  {specsList.map((spec) => (
                    <option key={spec.id} value={String(spec.id)}>
                      {spec.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {error && (
              <div className="spec-access-error">{error}</div>
            )}

            {selectedSpecId && (
              <>
                <form className="spec-access-grant-form" onSubmit={handleGrant}>
                  <input
                    type="text"
                    className="spec-access-grant-input"
                    placeholder="Username (e.g. john.doe)"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    disabled={granting}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={granting || !newUsername.trim()}
                  >
                    {granting ? 'Granting...' : 'Grant Access'}
                  </button>
                </form>

                {loading ? (
                  <div className="spec-access-loading">Loading access list...</div>
                ) : accessList.length === 0 ? (
                  <div className="spec-access-empty">No users have access to this spec.</div>
                ) : (
                  <div className="spec-access-table-wrapper">
                    <table className="spec-access-table">
                      <thead>
                        <tr>
                          <th>Username</th>
                          <th>Granted By</th>
                          <th>Created At</th>
                          <th>Default</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessList.map((access) => (
                          <tr key={access.id}>
                            <td className="spec-access-username">{access.username}</td>
                            <td>{access.grantedBy || '-'}</td>
                            <td>{access.createdAt ? new Date(access.createdAt).toLocaleString() : '-'}</td>
                            <td>
                              <button
                                type="button"
                                className={`btn btn-sm ${access.isDefault ? 'btn-default-active' : 'btn-default-inactive'}`}
                                onClick={() => handleSetDefault(access.username)}
                                title={access.isDefault ? 'This is the default spec' : 'Set as default spec'}
                              >
                                {access.isDefault ? 'Yes' : 'No'}
                              </button>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRevoke(access.username)}
                              >
                                Revoke
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </ModalBody>
      )}
      {ModalFooter && (
        <ModalFooter>
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

SpecAccessDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.object,
  backendSpecsSelectors: PropTypes.object,
};

export default SpecAccessDialog;
